import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { toast } from "sonner";
import { Radio, Sparkles, Check, RefreshCw, Loader2, ExternalLink, AlertTriangle, ArrowRightLeft } from "lucide-react";
import { PageHeader } from "@/components/layout/PageHeader";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Skeleton } from "@/components/ui/skeleton";
import { adsUnified, adsPrepare, adsApprove, adsRefreshMetrics, adsPropose, adsDecide, formatApiError } from "@/lib/api";

import { CampaignReviewEditor, StrategyCampaignImport, ChatGPTAdsAvailability } from "@/components/nadia/AdCampaignReview";
const CH = { meta: "Meta", tiktok: "TikTok", google: "Google Ads" };
const eur = (v) => (v == null ? "—" : `€ ${Number(v).toFixed(2)}`);
const num = (v) => (v == null ? "—" : Number(v).toLocaleString("it-IT"));

const PUBLICATION_LABELS = new Map([
  ["preparato", "Preparata · invio non confermato"],
  ["approvato", "Approvata · pubblicazione non confermata"],
  ["inviato", "Richiesta inviata · attività non confermata"],
  ["confermato_dal_provider", "Creazione confermata · attività non confermata"],
  ["attivo", "Attività dichiarata dall'API"],
  ["bloccato", "Operazione bloccata"],
  ["incerto", "Esito incompleto · incerto"],
  ["parziale", "Esito incompleto · parziale"],
  ["storico_non_verificato", "Stato storico non verificato"],
]);
const publicationLabel = (campaign) => PUBLICATION_LABELS.get(campaign?.publication_state) || "Stato operativo non confermato";

function PublicationBadge({ campaign }) {
  const incomplete = ["parziale", "incerto"].includes(campaign.publication_state);
  const color = incomplete ? "border-amber-500/30 text-amber-300"
    : campaign.publication_state === "confermato_dal_provider" ? "border-cyan-500/30 text-cyan-300"
    : "border-white/10 text-slate-300";
  return <span className={`text-[11px] px-2 py-0.5 rounded-full border ${color}`} data-testid={`campaign-publication-state-${campaign.id}`}>{publicationLabel(campaign)}</span>;
}

function Metric({ label, real, est }) {
  return (
    <div className="rounded-lg bg-white/[0.03] px-2.5 py-2">
      <div className="text-[10px] uppercase tracking-wider text-slate-500">{label}</div>
      <div className="text-sm text-white" data-testid="metric-real">{real}</div>
      {est != null && <div className="text-[10px] text-violet-300/80">stima {est}</div>}
    </div>
  );
}

function ChannelCard({ id, c }) {
  const r = c.real, e = c.estimate;
  return (
    <div className="nadia-card p-4 space-y-3" data-testid={`channel-card-${id}`}>
      <div className="flex items-center justify-between">
        <div className="font-heading font-semibold text-slate-100">{c.label}</div>
        <span className={`text-[11px] ${c.connected ? "text-emerald-300" : "text-slate-500"}`} data-testid={`channel-connection-summary-${id}`}>{c.connected ? `Collegato · ${c.campaigns_live} campagne registrate` : "non collegato"}</span>
      </div>
      {(c.account_name || c.account_id_ext) && (
        <div className="text-[11px] text-slate-400" data-testid={`channel-account-${id}`}>Account: <span className="text-slate-200">{c.account_name || c.account_id_ext}</span>{c.account_id_ext && <span className="text-slate-600"> · {c.account_id_ext}</span>}</div>
      )}
      <div className="flex flex-wrap gap-1.5" data-testid={`channel-readiness-${id}`}>
        <span className={`text-[10px] px-2 py-0.5 rounded-full border ${c.publish_ready ? "bg-emerald-500/15 text-emerald-200 border-emerald-500/30" : "border-amber-500/30 text-amber-300"}`}>
          {c.publish_ready ? "Pronto a pubblicare" : "Pubblicazione bloccata"}
        </span>
        {c.api_verified && <span className="text-[10px] px-2 py-0.5 rounded-full border border-cyan-500/30 text-cyan-300">Verificato via API</span>}
      </div>
      {!c.publish_ready && (c.blockers || []).length > 0 && (
        <ul className="text-[11px] text-amber-300/90 list-disc pl-4 space-y-0.5" data-testid={`channel-blockers-${id}`}>
          {c.blockers.map((b, i) => <li key={i}>{b}</li>)}
        </ul>
      )}
      <div className="flex gap-2 text-[10px]">
        <span className={`px-2 py-0.5 rounded-full border ${r ? "border-emerald-500/30 text-emerald-300" : "border-white/10 text-slate-500"}`}>DATI REALI {r ? "" : "assenti"}</span>
        {e && <span className="px-2 py-0.5 rounded-full border border-violet-500/30 text-violet-300">STIME NADIA</span>}
      </div>
      <div className="grid grid-cols-3 gap-2">
        <Metric label="Spesa" real={eur(r?.spend)} est={e && eur(e.spend)} />
        <Metric label="Impression" real={num(r?.impressions)} est={e && num(e.impressions)} />
        <Metric label="Click" real={num(r?.clicks)} est={e && num(e.clicks)} />
        <Metric label="CTR" real={r ? `${r.ctr}%` : "—"} est={e && `${e.ctr}%`} />
        <Metric label="CPC" real={eur(r?.cpc)} est={e && eur(e.cpc)} />
        <Metric label="Lead / CPL" real={r ? `${num(r.conversions)} · ${eur(r.cpa)}` : "—"} est={e && `${num(e.conversions)} · ${eur(e.cpa)}`} />
      </div>
      {r?.roas != null && <div className="text-xs text-slate-300">ROAS reale: {r.roas}x · ricavi {eur(r.revenue)}</div>}
    </div>
  );
}

export default function CanaliAds() {
  const [data, setData] = useState(null);
  const [busy, setBusy] = useState(null);
  const [channel, setChannel] = useState("");
  const [budget, setBudget] = useState("");
  const [googleType, setGoogleType] = useState("search");
  const [loadError, setLoadError] = useState("");
  const load = () => adsUnified().then(r=>{setData(r);setLoadError("");}).catch(e=>{setLoadError(formatApiError(e));});
  useEffect(() => { load(); }, []);

  const run = async (k, fn, ok) => {
    setBusy(k);
    try { const r = await fn(); if (ok) toast.success(typeof ok === "function" ? ok(r) : ok); await load(); return r; }
    catch (e) { toast.error(formatApiError(e)); } finally { setBusy(null); }
  };
  const prepare = () => run("prepare", () => adsPrepare({ channel: channel || null, daily_budget: budget ? Number(budget) : null, google_type: googleType }), (d) => `Bozza pronta su ${d.channel_label}: controlla e approva`);
  const approve = (d, paused = false, reviewToken = null) => {
    const msg = paused
      ? `Confermi la richiesta di creazione su ${d.channel_label} con PAUSA richiesta?\n${d.copy.headline}\nLa pausa effettiva e l'assenza di spesa non sono ancora verificate.`
      : `Confermi l'invio della richiesta di pubblicazione su ${d.channel_label}?\n${d.daily_budget} €/giorno · ${d.copy.headline}\nL'attivazione non è ancora confermata; la richiesta può comportare spesa.`;
    if (!window.confirm(msg)) return;
    run(`approve-${d.id}`, () => adsApprove(d.id, paused, reviewToken), (r) => `${publicationLabel(r)}${r?.channel_label ? ` · ${r.channel_label}` : ""}`);
  };

  if (!data) return loadError ? <div role="alert" className="nadia-card p-5 text-rose-300">Dati campagne non disponibili: {loadError}<Button onClick={load}>Riprova</Button></div> : <Skeleton className="h-96 bg-white/5" />;
  const drafts = data.campaigns.filter((c) => ["draft", "failed", "publishing"].includes(c.status));
  const live = data.campaigns.filter((c) => c.status === "published");
  const anyConnected = Object.values(data.channels).some((c) => c.connected);

  return (
    <>
      <PageHeader eyebrow="Canali pubblicitari" title="Meta · TikTok · Google Ads · ChatGPT Ads" subtitle="Nadia confronta i canali con dati reali, propone dove investire e pubblica solo dopo la tua approvazione." />
      {loadError && <p role="alert" className="mb-4 text-rose-300">Aggiornamento dati non riuscito: {loadError}. Invio disabilitato finché i dati non sono aggiornati.</p>}
      <StrategyCampaignImport onImported={load} />
      <ChatGPTAdsAvailability />
      <div className="nadia-card p-4 mb-5 flex items-start gap-3 border-cyan-500/20" data-testid="ads-insight">
        <Sparkles size={16} className="text-cyan-300 mt-0.5 shrink-0" /><div className="text-sm text-slate-200">{data.insight}</div>
      </div>
      {!anyConnected && (
        <div className="mb-5 text-xs text-amber-200 flex items-center gap-2" data-testid="ads-not-connected"><AlertTriangle size={14} /> Nessun account pubblicitario collegato. <Link to="/app/impostazioni" className="underline">Collega Meta, TikTok o Google Ads</Link> per pubblicare davvero.</div>
      )}
      <div className="grid gap-3 lg:grid-cols-3 mb-6" data-testid="channels-grid">
        {Object.entries(data.channels).map(([id, c]) => <ChannelCard key={id} id={id} c={c} />)}
      </div>

      <div className="flex flex-wrap gap-2 mb-6">
        <Button size="sm" variant="outline" className="border-white/10" onClick={() => run("refresh", adsRefreshMetrics, (r) => `Metriche reali aggiornate: ${r.updated} campagne`)} disabled={!!busy} data-testid="ads-refresh-metrics"><RefreshCw size={13} className="mr-1" /> Aggiorna dati reali</Button>
        <Button size="sm" variant="outline" className="border-white/10" onClick={() => run("propose", adsPropose, (r) => r.proposed ? "Nuova proposta di budget da approvare" : r.reason)} disabled={!!busy} data-testid="ads-propose"><ArrowRightLeft size={13} className="mr-1" /> Chiedi a Nadia dove spostare budget</Button>
      </div>

      {data.recommendations.length > 0 && (
        <div className="space-y-2 mb-6" data-testid="ads-recommendations">
          {data.recommendations.map((r) => (
            <div key={r.id} className="nadia-card p-4 border-violet-500/25 flex flex-col sm:flex-row sm:items-center gap-3 justify-between" data-testid={`rec-${r.id}`}>
              <div className="text-sm text-slate-200">{r.text}</div>
              <div className="flex gap-2 shrink-0">
                <Button size="sm" onClick={() => run(`rec-${r.id}`, () => adsDecide(r.id, true), "Spostamento approvato")} className="bg-emerald-500 text-slate-950" data-testid={`rec-approve-${r.id}`}><Check size={13} className="mr-1" /> Approva</Button>
                <Button size="sm" variant="outline" onClick={() => run(`recno-${r.id}`, () => adsDecide(r.id, false), "Proposta rifiutata")} className="border-white/10" data-testid={`rec-reject-${r.id}`}>No</Button>
              </div>
            </div>
          ))}
        </div>
      )}

      <div className="nadia-card p-5 mb-6 space-y-3" data-testid="ads-prepare">
        <div className="font-heading font-semibold text-slate-100 flex items-center gap-2"><Radio size={15} className="text-cyan-300" /> Nuova campagna</div>
        <div className="flex flex-wrap gap-2 items-end">
          <label className="text-xs text-slate-400 space-y-1"><span>Canale</span>
            <select value={channel} onChange={(e) => setChannel(e.target.value)} className="block rounded-md bg-white/[0.03] border border-white/10 px-3 py-2 text-sm text-slate-100" data-testid="ads-channel-select">
              <option value="">Lascia scegliere Nadia</option>{Object.entries(CH).map(([k, v]) => <option key={k} value={k}>{v}</option>)}
            </select></label>
          <label className="text-xs text-slate-400 space-y-1"><span>Budget €/giorno</span><Input value={budget} onChange={(e) => setBudget(e.target.value)} placeholder="dal piano" className="w-32 bg-white/[0.03] border-white/10" data-testid="ads-budget-input" /></label>
          {channel === "google" && <label className="text-xs text-slate-400">Formato<select aria-label="Formato Google" value={googleType} onChange={e=>setGoogleType(e.target.value)} className="block bg-slate-950 border border-white/10 p-2 rounded"><option value="search">Search</option><option value="display">Display (richiede asset Google)</option><option value="video">YouTube (pubblicazione da completare)</option></select></label>}
          <Button onClick={prepare} disabled={!!busy} className="bg-gradient-to-r from-cyan-400 to-violet-500 text-slate-950 font-semibold" data-testid="ads-prepare-button">{busy === "prepare" ? <Loader2 size={14} className="animate-spin mr-1" /> : <Sparkles size={14} className="mr-1" />} Prepara con Nadia</Button>
        </div>
      </div>

      {drafts.map((d) => (
        <div key={d.id} className="nadia-card p-5 mb-4 space-y-3" data-testid={`draft-${d.id}`}>
          <div className="flex flex-wrap items-center justify-between gap-2">
            <div><div className="font-heading font-semibold text-white">{d.name}</div><div className="text-xs text-slate-400">{d.channel_label}{d.google_type ? ` · ${d.google_type}` : ""} · obiettivo {d.objective} · {d.placements.join(", ")}</div></div>
            <PublicationBadge campaign={d} />
          </div>
          <div className="text-xs text-violet-300/90 bg-violet-500/[0.06] rounded-lg px-3 py-2">Nadia: {d.recommendation.why}</div>
          <CampaignReviewEditor draft={d} busy={busy || loadError} onApprove={approve} onSaved={load} />
          {d.error && <p className="text-sm text-rose-300">{d.error}</p>}
        </div>
      ))}
      {live.length > 0 && (
        <div className="space-y-2" data-testid="ads-live-list">
          <div className="font-heading font-semibold text-slate-100" data-testid="ads-campaign-outcomes-title">Esiti delle campagne</div>
          {live.map((c) => (
            <div key={c.id} className="nadia-card p-4 flex flex-wrap items-center justify-between gap-2 text-sm" data-testid={`live-${c.id}`}>
              <div className="space-y-1">
                <div className="text-white">{c.name}</div>
                <PublicationBadge campaign={c} />
                <div className="text-xs text-slate-400">
                  {c.channel_label} · ID {c.published?.campaign_id} · {c.daily_budget} €/giorno
                  {typeof c.requested_status === "string" && c.requested_status.length > 0 && <span data-testid={`campaign-requested-status-${c.id}`}> · richiesto: {c.requested_status}</span>}
                  {typeof c.observed_status === "string" && c.observed_status.length > 0 && <span data-testid={`campaign-observed-status-${c.id}`}> · osservato: {c.observed_status}</span>}
                </div>
              </div>
              <div className="flex items-center gap-3 text-xs">
                {c.metrics ? <span className="text-emerald-300">reale: {eur(c.metrics.spend)} · {num(c.metrics.clicks)} click · {num(c.metrics.conversions)} lead</span> : <span className="text-slate-500">metriche in arrivo</span>}
                {c.published?.url && <a href={c.published.url} target="_blank" rel="noreferrer" className="text-cyan-300 inline-flex items-center gap-1"><ExternalLink size={12} /> pannello</a>}
              </div>
            </div>
          ))}
        </div>
      )}
    </>
  );
}
